// src/components/layout/Layout.jsx
import React, { useState, useRef, useEffect, Suspense } from 'react';
import { Search, Bell, Sun, Moon, User, Settings, LogOut, TramFront } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import { PALETTE, THEME_CLASSES } from '../../lib/theme.js';
import { useAuth } from '../../context/AuthContext.jsx';

const LayoutFallback = ({ isDark }) => (
    <>
        <div
            className={`fixed inset-0 z-[60] cursor-wait backdrop-blur-[1px] transition-colors duration-300 ${
                isDark ? 'bg-black/40' : 'bg-[#F5F5F7]/40'
            }`}
        />
        <div className="relative z-[70] w-full h-full flex flex-col items-center justify-center min-h-[50vh] pointer-events-none">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: PALETTE.blue }}></div>
        </div>
    </>
);

export default function Layout({ isDark, setIsDark }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, startTour } = useAuth();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const profileRef = useRef(null);
    const toggleTheme = () => setIsDark(!isDark);

    useEffect(() => {
        function handleClickOutside(event) {
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setIsProfileOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getPageTitle = () => {
        const path = location.pathname;
        if (path === '/') return 'Overview';
        if (path === '/data-connectors') return 'Data Connectors';
        if (path === '/dimensions') return 'Dimensions';
        if (path === '/raw-tables') return 'Raw Tables';
        if (path === '/pipelines') return 'Pipelines';
        if (path === '/warehouse') return 'Warehouse';
        if (path === '/profile') return 'User Profile';
        if (path === '/settings') return 'Tenant Settings';
        if (path === '/reports') return 'Report Manager';
        return 'Integration Hub';
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length-1][0]}`.toUpperCase();
        return name.substring(0, 2).toUpperCase();
    };

    const handleLogoutClick = () => {
        setIsProfileOpen(false);
        setIsLogoutModalOpen(true);
    };

    const confirmLogout = () => {
        logout();
        navigate('/login');
    };

    const handlePlayTour = () => {
        const currentPath = location.pathname.toLowerCase();
        if (document.querySelector('.tour-details-tabs')) {
            startTour('ds_details_tour');
        } else if (document.querySelector('.tour-ds-collection')) {
            startTour('ds_add_tour');
        } else if (currentPath.includes('/data-connectors')) {
            startTour('ds_main_tour');
        } else if (document.querySelector('.tour-viewer-tabs')) {
            startTour('rt_viewer_tour');
        } else if (currentPath.includes('/raw-tables')) {
            startTour('rt_main_tour');
        } else if (document.querySelector('.tour-add-dim-root')) {
            startTour('add_dim_tour');
        } else if (document.querySelector('.tour-dim-viewer-root')) {
            startTour('dim_viewer_tour');
        } else if (document.querySelector('.tour-rel-root')) {
            startTour('dim_rel_tour');
        } else if (currentPath.includes('/dimensions')) {
            startTour('dimensions_main_tour');
        } else if (document.querySelector('.tour-pipe-builder-root')) {
            startTour('pipe_builder_tour');
        } else if (currentPath.includes('/pipelines')) {
            startTour('pipelines_main_tour');
        } else if (document.querySelector('.tour-wh-viewer-root')) {
            startTour('wh_viewer_tour');
        } else if (document.querySelector('.tour-wh-root')) {
            startTour('wh_main_tour');
        } else {
            navigate('/');
            startTour('intro_tour');
        }
    };

    // --- Admin Impersonation Logic ---
    const isSuperAdmin = user?.is_super_admin;
    const orgName = user?.organization_name;

    const exitImpersonation = () => {
        // Strip the mocked tenant data from local storage to drop the disguise
        localStorage.removeItem('session_token');
        localStorage.removeItem('tenant_id');
        localStorage.removeItem('user_data');
        localStorage.removeItem('user_email');
        localStorage.removeItem('tenant_status');

        // Hard route directly back to the Command Center
        window.location.href = '/admin';
    };

    // If impersonating, flood the header background with theme blue.
    const headerBgStyle = isSuperAdmin
        ? 'bg-[#3160F4] border-transparent'
        : `${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}`;

    return (
        <div
            className={`h-screen overflow-hidden flex transition-colors duration-500 ${THEME_CLASSES.bgMain(isDark)}`}
            style={{ fontFamily: PALETTE.font }}
        >
            <Sidebar isDark={isDark} />
            <div className="flex-1 flex flex-col min-w-0 relative">

                <header className={`h-12 backdrop-blur-xl border-b sticky top-0 z-[60] transition-colors duration-500 ${headerBgStyle}`}>
                    <div className="max-w-7xl mx-auto w-full h-full px-4 flex items-center justify-between">

                        <h1 className={`text-lg font-bold tracking-tight flex items-center ${isSuperAdmin ? 'text-white' : THEME_CLASSES.textPrimary(isDark)}`}>
                            {getPageTitle()}
                            {isSuperAdmin && orgName && (
                                <>
                                    <span className={`mx-3 text-sm font-normal ${isSuperAdmin ? 'text-white/40' : THEME_CLASSES.textSecondary(isDark)}`}>|</span>
                                    <span className="text-sm font-semibold text-emerald-400">{orgName}</span>
                                </>
                            )}
                        </h1>

                        <div className="flex items-center space-x-4">

                            {/* Conditionally swap the Tour button for the Exit button */}
                            {isSuperAdmin ? (
                                <button
                                    onClick={exitImpersonation}
                                    className="px-3 py-1.5 rounded-lg flex items-center gap-2 text-xs font-bold transition-all bg-white/20 hover:bg-white/30 text-white shadow-sm"
                                >
                                    <LogOut size={14} /> Exit Workspace
                                </button>
                            ) : (
                                <button
                                    onClick={handlePlayTour}
                                    title="Replay Page Tour"
                                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${THEME_CLASSES.bgHighlight(isDark)} ${THEME_CLASSES.textPrimary(isDark)} hover:opacity-70`}
                                >
                                    <TramFront size={16} />
                                </button>
                            )}

                            <button
                                onClick={toggleTheme}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isSuperAdmin ? 'bg-white/20 text-white hover:bg-white/30' : `${THEME_CLASSES.bgHighlight(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`}`}
                            >
                                {isDark ? <Sun size={16} /> : <Moon size={16} />}
                            </button>
                            <div className="relative" ref={profileRef}>
                                <button
                                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                                    className={`tour-nav-profile w-8 h-8 rounded-full border flex items-center justify-center text-xs font-bold shadow-sm transition-all hover:opacity-80 ${isSuperAdmin ? 'bg-white/20 border-white/30 text-white' : `${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`}`}
                                >
                                    {getInitials(user?.name)}
                                </button>
                                {isProfileOpen && (
                                    <div className={`absolute right-0 mt-3 w-56 rounded-xl shadow-xl border overflow-hidden backdrop-blur-xl animate-in fade-in slide-in-from-top-2 z-50 ${THEME_CLASSES.bgDrawer(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}`}>
                                        <div className={`px-4 py-3 border-b ${THEME_CLASSES.borderSubtle(isDark)}`}>
                                            <p className={`text-sm font-semibold truncate ${THEME_CLASSES.textPrimary(isDark)}`}>{user?.name || 'User'}</p>
                                            <p className={`text-xs truncate ${THEME_CLASSES.textSecondary(isDark)}`}>{user?.email || 'user@example.com'}</p>
                                        </div>
                                        <div className="p-1.5">
                                            <button
                                                onClick={() => { setIsProfileOpen(false); navigate('/profile'); }}
                                                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${THEME_CLASSES.bgHover(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`}
                                            >
                                                <User size={16} className={THEME_CLASSES.textSecondary(isDark)} />
                                                <span>User Profile</span>
                                            </button>

                                            {/* Admin only check for Settings */}
                                            {(user?.role === 'admin' || user?.role === 'superadmin' || user?.is_super_admin) && (
                                                <button
                                                    onClick={() => { setIsProfileOpen(false); navigate('/settings'); }}
                                                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${THEME_CLASSES.bgHover(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`}
                                                >
                                                    <Settings size={16} className={THEME_CLASSES.textSecondary(isDark)} />
                                                    <span>Tenant Settings</span>
                                                </button>
                                            )}
                                        </div>
                                        <div className={`p-1.5 border-t ${THEME_CLASSES.borderSubtle(isDark)}`}>
                                            <button
                                                onClick={handleLogoutClick}
                                                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${THEME_CLASSES.bgHover(isDark)} ${THEME_CLASSES.textSecondary(isDark)} hover:${isDark ? 'text-white' : 'text-black'}`}
                                            >
                                                <LogOut size={16} />
                                                <span>Sign Out</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>
                <main className="flex-1 p-2 md:p-3 overflow-y-auto relative">
                    <div className="max-w-7xl mx-auto w-full min-h-full">
                        <Suspense fallback={<LayoutFallback isDark={isDark} />}>
                            <Outlet />
                        </Suspense>
                    </div>
                </main>
            </div>
            {isLogoutModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className={`w-full max-w-sm p-6 rounded-2xl shadow-2xl border ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)} animate-in zoom-in-95 duration-200`}>
                        <h3 className={`text-lg font-bold mb-2 ${THEME_CLASSES.textPrimary(isDark)}`}>Sign Out</h3>
                        <p className={`text-sm mb-6 ${THEME_CLASSES.textSecondary(isDark)}`}>
                            Are you sure you want to sign out of your account?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setIsLogoutModalOpen(false)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${THEME_CLASSES.bgHover(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmLogout}
                                className="px-4 py-2 rounded-xl text-sm font-medium text-white hover:opacity-80 transition-opacity shadow-sm"
                                style={{ backgroundColor: PALETTE.red || '#ef4444' }}
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
